<template lang="pug">
  v-layout(v-show='expandDescription' align-start justify-start row wrap).main-layout
    liondesk-dialog(
      :value="showConfirmationDialog"
      dialog-title="Remove Contact Relationship"
      ok-button-text="Remove"
      @click:cancel="resetDelete"
      @click:ok="deleteRelationship")
      template(slot="body")
        span
          | Are you sure you want to remove
          strong &nbsp{{ getRelatedContactName }}&nbsp
          | from
          strong &nbsp{{ getMainContactName }}&nbsp
          | as a contact relationship?
    v-expansion-panel(
      v-model="openedPanels"
      expand
      class="grey lighten-3"
    )
      v-expansion-panel-content(
        v-for="(relationship, index) in displayableRelationships"
        :key="getContactInfo(relationship).id"
      ).expansion-panel
        template(slot="actions")
          v-icon(color="#235ea1") keyboard_arrow_down
        template(slot="header")
          v-layout(align-center)
            v-flex.xs1.pr-5.ml-2
              v-layout
                liondesk-avatar(
                  :color="getTitleCardName(relationship) | getInitials | generateRandomColor('dark')"
                  :text="getTitleCardName(relationship) | getInitials"
                  text-classes="white--text"
                  text-styles="font-size: 15px;"
                  size="45px"
                )
            v-flex.xs12.pl-1
              v-layout(justify-space-between)
                span(@click.stop="goToProfile(relationship)").panel-name
                  | {{ getTitleCardName(relationship) }}
                v-icon(
                  v-if="showDeleteBtn(index)"
                  size="20"
                  @click.stop="openDeleteConfirmation(index)") delete
              v-layout
                v-flex
                  div(v-if="!isPanelOpened(index)" justify-start).panel-description {{ relationship.relationship_description }}
        v-card
          v-card-text
            v-layout
              v-flex(offset-xs1).pl-2
                v-layout(column)
                  v-flex(xs12)
                    v-select(
                      :value="relationship.relationship"
                      :items="relationshipTypesFilter(relationship.relationship)"
                      :placeholder="relationship.relationship_description"
                      :disabled="!isMainContactOwned || !isMainContact(relationship)"
                      item-text="name"
                      item-value="id"
                      item-disabled="disabled"
                      @change="changedRelType($event, relationship)"
                      label="Relationship"
                    )
                  v-flex
                    v-layout(align-center row).py-1.info-layout
                      v-flex(xs2)
                        span.field-title Email
                      v-flex(xs6).field-text
                        span {{ getContactInfo(relationship).email }}
                      v-flex(v-if="getContactInfo(relationship).email" xs4)
                        v-btn(small color="primary" @click="showDrawerEmail(getContactInfo(relationship))").action-btn
                          v-icon(size="14").pr-1 email
                          span Email
                    v-layout(align-center row).py-1.info-layout
                      v-flex(xs2)
                        span.field-title Home
                      v-flex(xs6).field-text
                        span {{ getContactInfo(relationship).home_phone }}
                    v-layout(align-center row).py-1.info-layout
                      v-flex(xs2)
                        span.field-title Mobile
                      v-flex(xs6).field-text
                        span {{ getContactInfo(relationship).mobile_phone }}
                      v-flex(v-if="getContactInfo(relationship).mobile_phone" xs4)
                        v-btn(small color="primary" @click="showDrawerText(getContactInfo(relationship))").action-btn
                          v-icon(size="14").pr-1 smartphone
                          span Text
                    v-layout(align-center row).py-1.info-layout
                      v-flex(xs2)
                        span.field-title Office
                      v-flex(xs6).field-text
                        span {{ getContactInfo(relationship).office_phone }}
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import LiondeskAvatar from '@/Core/components/LiondeskAvatar';
import getInitials from '@/Core/filters/getInitials';
import generateRandomColor from '@/Core/utilities/randomColorGenerator';
import { RELATION_TYPE_SPOUSE } from '@/Contacts/store/contact-relationships';

const LiondeskDialog = () =>
  import(/* webpackChunkName: 'LiondeskDialog' */ '@/Core/components/LiondeskDialog');

export default {
  name: "ContactRelationshipsView",
  components: {
    LiondeskAvatar,
    LiondeskDialog,
  },
  filters: {
    generateRandomColor,
    getInitials,
  },
  props: {
    expandDescription: {
      type: Boolean,
      default: false,
    },
    viewAll: {
      type: Boolean,
      default: false,
    },
    isMainContactOwned: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      openedPanels: [],
      changedRelationshipsType: [],
      showConfirmationDialog: false,
      relationshipToDelete: null,
    }
  },
  computed: {
    ...mapState('contactRelationships', ['relatedContacts', 'relationshipTypes']),
    ...mapGetters('contactRelationships', ['relationshipTypesWithName', 'relationshipsSpouse']),
    // This two methods are for the delete modal description
    getMainContactName() {
      if (this.relationshipToDelete === null) return '';
      const { main_contact } = this.sortedRelationships[this.relationshipToDelete];
      return this.getContactName(main_contact);
    },
    getRelatedContactName() {
      if (this.relationshipToDelete === null) return '';
      const { related_contact } = this.sortedRelationships[this.relationshipToDelete];
      return this.getContactName(related_contact);
    },
    sortedRelationships() {
      return [...this.relatedContacts].sort((firstRel, secondRel) => {
        const fRel = this.getContactInfo(firstRel);
        const sRel = this.getContactInfo(secondRel);
        if (fRel.first_name === "") return 1;
        if (sRel.first_name === "") return -1;
        return fRel.first_name.localeCompare(sRel.first_name);
      });
    },
    displayableRelationships() {
      return this.viewAll ? this.sortedRelationships : this.sortedRelationships.slice(0, 5);
    }
  },
  methods: {
    ...mapActions('contactRelationships', ['deleteContactRelationship', 'setRelationshipsTypeChanges']),
    ...mapActions('recipients', ['setToSelected']),
    goToProfile(relationship) {
      const { id: contactId } = this.getContactInfo(relationship);
      const routeData = this.$router.resolve({ name: 'contact-profile', params: { id: contactId } });
      window.open(routeData.href, '_blank');
    },
    isPanelOpened(index) {
      return this.openedPanels[index];
    },
    changedRelType(relTypeId, relationship)  {
      const change = {
        contact_id: relationship.related_contact.id,
        relationship_id: relationship.relationship.id,
        contact_type_id: relTypeId,
      }
      this.setRelationshipsTypeChanges(change);
    },
    contactHasName(currentContact) {
      return currentContact.first_name || currentContact.last_name;
    },
    getContactName(currentContact) {
      if (!this.contactHasName(currentContact)) return '(Unnamed Contact)';

      return `${currentContact.first_name || ''} ${currentContact.last_name || ''}`;
    },
    getTitleCardName(relationship) {
      const currentContact = this.getContactInfo(relationship);
      return this.getContactName(currentContact);
    },
    openDeleteConfirmation(relationshipIndex) {
      this.relationshipToDelete = relationshipIndex;
      this.showConfirmationDialog = true;
    },
    async deleteRelationship() {
      const { relationship } = this.sortedRelationships[this.relationshipToDelete];
      await this.deleteContactRelationship({
        contactId: this.$route.params.id,
        relationshipId: relationship.id,
      });
      this.resetDelete();
      this.$emit('refresh-relationships');
    },
    resetDelete() {
      this.showConfirmationDialog = false;
      this.relationshipToDelete = null;
    },
    showDeleteBtn(index) {
      const { related_contact } = this.sortedRelationships[index];
      return this.isPanelOpened(index) && this.$route.params.id != related_contact.id && this.isMainContactOwned;
    },
    getContactInfo(relationship) {
      const currentContactId = this.$route.params.id;
      const { main_contact: mainContact, related_contact: relatedContact } = relationship;

      return mainContact.id != currentContactId ? mainContact : relatedContact;
    },
    showDrawerEmail(contact) {
      this.setToSelected([contact]);
      this.$emit('openEmailDrawer');
    },
    showDrawerText(contact) {
      this.$emit('showChatDrawer', contact.id);
    },
    relationshipTypesFilter(relationship) {
      if (this.relationshipsSpouse.length && relationship.name !== RELATION_TYPE_SPOUSE) {
        return this.relationshipTypesWithName.map(r => {
            r.disabled = r.name === RELATION_TYPE_SPOUSE;
            return r;
          });
      };
      return this.relationshipTypesWithName.map(relationship => { return {...relationship, disabled: false}} );
    },
    isMainContact(relationship) {
      const { main_contact: mainContact } = relationship;
      const currentContactId = this.$route.params.id;
      return mainContact.id == currentContactId
    },
  }
}
</script>

<style lang="scss" scoped>
.main-layout {
  overflow-y: scroll;
  max-height: 35vh;
}

.info-layout {
  max-height: 29px;
}

.field-text {
  overflow: hidden;
  text-overflow: ellipsis;
}

.panel-description {
  opacity: 0.6;
  font-size: 12px;
  font-weight: bold;
}

::v-deep {
  .field-title {
    opacity: 0.6;
  }

  .v-card__text {
    padding: 2px 16px;
  }

  .v-expansion-panel__header {
    padding: 10px 5px;
  }

  .v-text-field {
    padding-top: 10px;
    margin-top: 0;
  }

  .v-btn--small {
    padding: 0 8px;
    height: 20px;
  }

  .v-btn {
    margin: 0 8px;
    font-weight: 2;
  }
}

.action-btn {
  font-size: 12px;
}

.panel-name {
  color: var(--v-primary-base);
  font-size: 14px;
  font-weight: bold;
  letter-spacing: 0.51px;
  line-height: 19px;
}

.expansion-panel {
  border-bottom: 1px solid var(--v-background-lighten3);
}
</style>

<style lang="scss">
#contact-relationships {
  .theme--light.v-text-field.v-input--is-disabled .v-input__control .v-input__slot::before {
    border-image: none;
  }

  .v-text-field input::placeholder {
    color: rgba(0,0,0,0.87);
  }

  .theme--light.v-text-field.v-input--is-disabled .v-input__control .v-input__slot .v-select__slot .v-input__append-inner {
    display: none;
  }
}
</style>